import { useState, useEffect, useRef } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { Button } from "../components/ui/button";
import { Card, CardContent } from "../components/ui/card";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { useAuth } from "../App";
import { toast } from "sonner";
import {
  Building2, ArrowLeft, Send, Bot, User, Loader2, 
  Sparkles, Leaf, Waves, Wind, Wrench, MapPin, Calendar, Image
} from "lucide-react";

const API_URL = process.env.REACT_APP_BACKEND_URL;

const CATEGORY_CONFIG = {
  cat_diarista: { icon: Sparkles, name: "Diarista", color: "bg-pink-100 text-pink-600" },
  cat_jardinagem: { icon: Leaf, name: "Jardinagem", color: "bg-green-100 text-green-600" },
  cat_piscina: { icon: Waves, name: "Piscina", color: "bg-blue-100 text-blue-600" },
  cat_arcondicionado: { icon: Wind, name: "Ar-Condicionado", color: "bg-cyan-100 text-cyan-600" },
  cat_reforma: { icon: Wrench, name: "Reforma Leve", color: "bg-orange-100 text-orange-600" },
  ai: { icon: Bot, name: "Assistente IA", color: "bg-emerald-100 text-emerald-600" }
};

export default function DiagnosticPage() {
  const { categoryId } = useParams();
  const { token, user } = useAuth();
  const navigate = useNavigate();
  const [messages, setMessages] = useState([]);
  const [inputMessage, setInputMessage] = useState("");
  const [loading, setLoading] = useState(false);
  const [sessionId] = useState(`diag_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`);
  const [showForm, setShowForm] = useState(false);
  const [formData, setFormData] = useState({
    title: "",
    description: "",
    address: { street: "", city: "", state: "SP", zip: "" },
    preferred_dates: []
  });
  const messagesEndRef = useRef(null);

  const config = CATEGORY_CONFIG[categoryId] || CATEGORY_CONFIG.ai;
  const Icon = config.icon;

  useEffect(() => {
    // Initial AI message
    const initialMessage = categoryId === "ai" 
      ? "Olá! Sou o assistente eZelo. Descreva o que você precisa e vou ajudar a encontrar o serviço ideal para sua casa."
      : `Olá! Vou ajudar você a solicitar um serviço de ${config.name}. Para começar, me conte um pouco sobre o que você precisa. Por exemplo: qual o tamanho do espaço? Com que frequência você precisa do serviço?`;
    
    setMessages([
      {
        role: "assistant",
        content: initialMessage,
        timestamp: new Date()
      }
    ]);
  }, [categoryId, config.name]);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages]);

  const sendMessage = async () => {
    if (!inputMessage.trim() || loading) return;

    const userMessage = {
      role: "user",
      content: inputMessage,
      timestamp: new Date()
    };

    setMessages(prev => [...prev, userMessage]);
    setInputMessage("");
    setLoading(true);

    try {
      const response = await fetch(`${API_URL}/api/ai/diagnostic`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          ...(token && { Authorization: `Bearer ${token}` })
        },
        body: JSON.stringify({
          session_id: sessionId,
          message: inputMessage,
          context: { category_id: categoryId !== "ai" ? categoryId : null },
          mode: "guided"
        })
      });

      if (!response.ok) {
        throw new Error("Erro na comunicação com IA");
      }

      const data = await response.json();
      
      setMessages(prev => [...prev, {
        role: "assistant",
        content: data.response,
        timestamp: new Date()
      }]);

      // Check if AI suggests creating a request
      if (data.response.toLowerCase().includes("criar") && data.response.toLowerCase().includes("solicitação") ||
          data.response.toLowerCase().includes("informações suficientes") ||
          messages.length >= 6) {
        setShowForm(true);
      }
    } catch (error) {
      toast.error("Erro ao processar mensagem. Tente novamente.");
      console.error("AI error:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      sendMessage();
    }
  };

  const submitRequest = async () => {
    if (!formData.title || !formData.description || !formData.address.city) {
      toast.error("Preencha os campos obrigatórios");
      return;
    }

    setLoading(true);
    try {
      const response = await fetch(`${API_URL}/api/services/requests`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify({
          category_id: categoryId !== "ai" ? categoryId : "cat_diarista",
          title: formData.title,
          description: formData.description + "\n\n--- Contexto do Diagnóstico ---\n" + messages.map(m => `${m.role}: ${m.content}`).join("\n"),
          address: formData.address,
          preferred_dates: formData.preferred_dates,
          photos: []
        })
      });

      if (!response.ok) {
        throw new Error("Erro ao criar solicitação");
      }

      toast.success("Solicitação criada com sucesso!");
      navigate(`/providers/${categoryId !== "ai" ? categoryId : "cat_diarista"}`);
    } catch (error) {
      toast.error(error.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-stone-50 flex flex-col" data-testid="diagnostic-page">
      {/* Header */}
      <header className="bg-white border-b border-slate-200 sticky top-0 z-40">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <div className="flex items-center gap-4">
              <Button variant="ghost" size="icon" onClick={() => navigate("/discovery")} data-testid="back-btn">
                <ArrowLeft className="w-5 h-5" />
              </Button>
              <div className="flex items-center gap-2">
                <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${config.color}`}>
                  <Icon className="w-5 h-5" />
                </div>
                <div>
                  <p className="font-semibold text-slate-900">{config.name}</p>
                  <p className="text-xs text-slate-500">Diagnóstico com IA</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </header>

      {/* Chat Messages */}
      <main className="flex-1 max-w-4xl w-full mx-auto px-4 sm:px-6 lg:px-8 py-6 overflow-y-auto">
        <div className="space-y-4">
          {messages.map((message, index) => (
            <div
              key={index}
              className={`flex ${message.role === "user" ? "justify-end" : "justify-start"} animate-fade-in`}
            >
              <div className={`flex items-start gap-3 max-w-[85%] ${message.role === "user" ? "flex-row-reverse" : ""}`}>
                <div className={`w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0 ${
                  message.role === "user" ? "bg-slate-900" : "bg-emerald-100"
                }`}>
                  {message.role === "user" ? (
                    <User className="w-4 h-4 text-white" />
                  ) : (
                    <Bot className="w-4 h-4 text-emerald-600" />
                  )}
                </div>
                <div className={`rounded-2xl px-4 py-3 ${
                  message.role === "user" 
                    ? "bg-slate-900 text-white rounded-br-md" 
                    : "bg-white shadow-sm border border-slate-200 rounded-bl-md"
                }`}>
                  <p className="text-sm whitespace-pre-wrap">{message.content}</p>
                </div>
              </div>
            </div>
          ))}
          
          {loading && (
            <div className="flex justify-start animate-fade-in">
              <div className="flex items-start gap-3">
                <div className="w-8 h-8 rounded-full bg-emerald-100 flex items-center justify-center">
                  <Bot className="w-4 h-4 text-emerald-600" />
                </div>
                <div className="bg-white shadow-sm border border-slate-200 rounded-2xl rounded-bl-md px-4 py-3">
                  <div className="flex items-center gap-2">
                    <Loader2 className="w-4 h-4 animate-spin text-slate-400" />
                    <span className="text-sm text-slate-400">Pensando...</span>
                  </div>
                </div>
              </div>
            </div>
          )}
          
          <div ref={messagesEndRef} />
        </div>

        {/* Request Form */}
        {showForm && (
          <Card className="mt-6 animate-fade-in">
            <CardContent className="p-6">
              <h3 className="text-lg font-semibold text-slate-900 mb-4">Criar Solicitação</h3>
              <div className="space-y-4">
                <div>
                  <label className="text-sm font-medium text-slate-700 mb-1 block">Título da Solicitação *</label>
                  <Input
                    placeholder="Ex: Limpeza completa apartamento 80m²"
                    value={formData.title}
                    onChange={(e) => setFormData({...formData, title: e.target.value})}
                    data-testid="request-title-input"
                  />
                </div>
                <div>
                  <label className="text-sm font-medium text-slate-700 mb-1 block">Descrição *</label>
                  <Textarea
                    placeholder="Descreva detalhes adicionais..."
                    value={formData.description}
                    onChange={(e) => setFormData({...formData, description: e.target.value})}
                    rows={3}
                    data-testid="request-description-input"
                  />
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="text-sm font-medium text-slate-700 mb-1 block">Cidade *</label>
                    <Input
                      placeholder="São Paulo"
                      value={formData.address.city}
                      onChange={(e) => setFormData({...formData, address: {...formData.address, city: e.target.value}})}
                      data-testid="request-city-input"
                    />
                  </div>
                  <div>
                    <label className="text-sm font-medium text-slate-700 mb-1 block">Bairro</label>
                    <Input
                      placeholder="Jardins"
                      value={formData.address.street}
                      onChange={(e) => setFormData({...formData, address: {...formData.address, street: e.target.value}})}
                    />
                  </div>
                </div>
                <Button 
                  className="w-full bg-slate-900 hover:bg-slate-800"
                  onClick={submitRequest}
                  disabled={loading}
                  data-testid="submit-request-btn"
                >
                  {loading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Send className="w-4 h-4 mr-2" />}
                  Criar Solicitação e Ver Prestadores
                </Button>
              </div>
            </CardContent>
          </Card>
        )}
      </main>

      {/* Input Area */}
      <div className="bg-white border-t border-slate-200 sticky bottom-0">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex items-end gap-3">
            <div className="flex-1 relative">
              <Textarea
                placeholder="Digite sua mensagem..."
                value={inputMessage}
                onChange={(e) => setInputMessage(e.target.value)}
                onKeyPress={handleKeyPress}
                className="resize-none pr-12 min-h-[48px] max-h-32"
                rows={1}
                disabled={loading}
                data-testid="chat-input"
              />
            </div>
            <Button 
              onClick={sendMessage}
              disabled={!inputMessage.trim() || loading}
              className="h-12 w-12 p-0 bg-slate-900 hover:bg-slate-800"
              data-testid="send-btn"
            >
              {loading ? <Loader2 className="w-5 h-5 animate-spin" /> : <Send className="w-5 h-5" />}
            </Button>
          </div>
          <p className="text-xs text-slate-400 mt-2 text-center">
            IA powered by OpenAI GPT-5.2 • Suas informações são protegidas
          </p>
        </div>
      </div>
    </div>
  );
}
